import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { audienceInsights } from '../data/mock';

// Custom hook for counting animation
const useCountUp = (end, duration = 2000, startCounting = false) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!startCounting) return;
    
    let startTime = null;
    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      setCount(Math.floor(progress * end));
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    requestAnimationFrame(animate);
  }, [end, duration, startCounting]);

  return count;
};

// Donut Chart Component
const DonutChart = ({ male, female, isVisible }) => {
  const [offset, setOffset] = useState(282.7);
  const malePercentage = male;
  const circumference = 282.7;

  useEffect(() => {
    if (isVisible) {
      const timer = setTimeout(() => {
        setOffset(circumference - (malePercentage / 100) * circumference);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isVisible, malePercentage]);

  return (
    <div className="relative w-40 h-40 mx-auto">
      <svg className="w-full h-full transform -rotate-90">
        {/* Background circle (female) */}
        <circle
          cx="80"
          cy="80"
          r="45"
          stroke="#2563EB"
          strokeWidth="14"
          fill="transparent"
          className="opacity-80"
        />
        {/* Foreground circle (male) */}
        <circle
          cx="80"
          cy="80"
          r="45"
          stroke="#FF3B3B"
          strokeWidth="14"
          fill="transparent"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          className="transition-all duration-1000 ease-out"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-white text-xl font-bold">{male}%</span>
        <span className="text-[#9CA3AF] text-xs">Male</span>
      </div>
    </div>
  );
};

// Progress Bar Component
const ProgressBar = ({ label, percentage, isVisible, delay = 0 }) => {
  const [width, setWidth] = useState(0);

  useEffect(() => {
    if (isVisible) {
      const timer = setTimeout(() => {
        setWidth(percentage);
      }, delay);
      return () => clearTimeout(timer);
    }
  }, [isVisible, percentage, delay]);

  return (
    <div className="mb-4">
      <div className="flex justify-between mb-2">
        <span className="text-[#9CA3AF] text-sm">{label}</span>
        <span className="text-white font-semibold text-sm">{percentage}%</span>
      </div>
      <div className="h-2 bg-[#1A1A1A] rounded-full overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-[#FF3B3B] to-[#E02F2F] rounded-full transition-all duration-1000 ease-out"
          style={{ width: `${width}%` }}
        />
      </div>
    </div>
  );
};

// City Bar Component
const CityBar = ({ city, percentage, isVisible, delay = 0 }) => {
  const [width, setWidth] = useState(0);

  useEffect(() => {
    if (isVisible) {
      const timer = setTimeout(() => {
        setWidth(percentage);
      }, delay);
      return () => clearTimeout(timer);
    }
  }, [isVisible, percentage, delay]);

  return (
    <div className="flex items-center gap-3 mb-3">
      <span className="text-[#9CA3AF] text-sm w-24 flex-shrink-0">{city}</span>
      <div className="flex-1 h-2 bg-[#1A1A1A] rounded-full overflow-hidden">
        <div
          className="h-full bg-[#2563EB] rounded-full transition-all duration-1000 ease-out"
          style={{ width: `${(width / 18) * 100}%` }}
        />
      </div>
      <span className="text-white font-semibold text-sm w-10 text-right">{percentage}%</span>
    </div>
  );
};

const AudienceInsights = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section id="audience" className="bg-[#0B0B0B] py-24" ref={sectionRef}>
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="text-[#FF3B3B] text-sm font-semibold uppercase tracking-widest mb-4 block">
            Audience Analytics
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Know Your Target Market
          </h2>
          <p className="text-[#9CA3AF] text-lg max-w-2xl mx-auto">
            Deep insights into my highly engaged fitness community—your ideal customers.
          </p>
        </div>

        {/* Dashboard Cards */}
        <div className="grid md:grid-cols-3 gap-6">
          {/* Gender Split Card */}
          <Card className={`bg-[#111111] border-white/5 rounded-2xl overflow-hidden transition-all duration-500 hover:-translate-y-1 hover:border-[#FF3B3B]/20 hover:shadow-lg hover:shadow-[#FF3B3B]/5 ${isVisible ? 'animate-fadeInUp' : 'opacity-0'}`}>
            <CardHeader className="pb-2">
              <CardTitle className="text-white text-lg font-semibold">Gender Split</CardTitle>
              <p className="text-[#9CA3AF] text-sm">Audience composition</p>
            </CardHeader>
            <CardContent className="pt-4">
              <DonutChart
                male={audienceInsights.genderSplit.male}
                female={audienceInsights.genderSplit.female}
                isVisible={isVisible}
              />
              <div className="flex justify-center gap-6 mt-6">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-[#FF3B3B]" />
                  <span className="text-[#9CA3AF] text-sm">Male {audienceInsights.genderSplit.male}%</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-[#2563EB]" />
                  <span className="text-[#9CA3AF] text-sm">Female {audienceInsights.genderSplit.female}%</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Age Distribution Card */}
          <Card className={`bg-[#111111] border-white/5 rounded-2xl overflow-hidden transition-all duration-500 hover:-translate-y-1 hover:border-[#FF3B3B]/20 hover:shadow-lg hover:shadow-[#FF3B3B]/5 ${isVisible ? 'animate-fadeInUp animation-delay-100' : 'opacity-0'}`}>
            <CardHeader className="pb-2">
              <CardTitle className="text-white text-lg font-semibold">Age Distribution</CardTitle>
              <p className="text-[#9CA3AF] text-sm">Primary demographic: 25-34</p>
            </CardHeader>
            <CardContent className="pt-4">
              {audienceInsights.ageDistribution.map((item, index) => (
                <ProgressBar
                  key={item.range}
                  label={item.range}
                  percentage={item.percentage}
                  isVisible={isVisible}
                  delay={index * 150}
                />
              ))}
            </CardContent>
          </Card>

          {/* Top Cities Card */}
          <Card className={`bg-[#111111] border-white/5 rounded-2xl overflow-hidden transition-all duration-500 hover:-translate-y-1 hover:border-[#FF3B3B]/20 hover:shadow-lg hover:shadow-[#FF3B3B]/5 ${isVisible ? 'animate-fadeInUp animation-delay-200' : 'opacity-0'}`}>
            <CardHeader className="pb-2">
              <CardTitle className="text-white text-lg font-semibold">Top US Cities</CardTitle>
              <p className="text-[#9CA3AF] text-sm">Geographic concentration</p>
            </CardHeader>
            <CardContent className="pt-4">
              {audienceInsights.topCities.map((item, index) => (
                <CityBar
                  key={item.city}
                  city={item.city}
                  percentage={item.percentage}
                  isVisible={isVisible}
                  delay={index * 150}
                />
              ))}
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default AudienceInsights;
